/** Add / edit a partner contact (react-hook-form + zod). The claim link for the
 *  partner's developer key goes to an active technical contact. */
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, FormControlLabel, MenuItem, Switch, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { addContact, updateContact, type PartnerContact } from '@/api/partners';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';

import { partnerErrorMessage } from './partnerErrors';

const ContactSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255),
  email: z.string().trim().email('Not a valid e-mail address').max(254),
  phone: z.string().trim().max(64),
  role: z.enum(['technical', 'business']),
  is_active: z.boolean(),
});

type ContactValues = z.infer<typeof ContactSchema>;

const EMPTY: ContactValues = { name: '', email: '', phone: '', role: 'technical', is_active: true };

interface Props {
  open: boolean;
  partnerId: number;
  /** null = add; a contact = edit. */
  contact: PartnerContact | null;
  onClose: () => void;
}

export function PartnerContactDialog({ open, partnerId, contact, onClose }: Props) {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const isEdit = contact !== null;
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactValues>({ resolver: zodResolver(ContactSchema), defaultValues: EMPTY });

  useEffect(() => {
    if (!open) return;
    reset(
      contact
        ? { name: contact.name, email: contact.email, phone: contact.phone, role: contact.role, is_active: contact.is_active }
        : EMPTY,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact?.id]);

  const mutation = useMutation({
    mutationFn: (v: ContactValues) =>
      isEdit ? updateContact(partnerId, contact.id, v) : addContact(partnerId, v),
    onSuccess: () => {
      snackbar.show(isEdit ? 'Contact updated.' : 'Contact added.', 'success');
      void qc.invalidateQueries({ queryKey: ['partners'] });
      onClose();
    },
    onError: (e) => snackbar.show(partnerErrorMessage(e), 'error'),
  });

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit contact — ${contact.name}` : 'Add contact'}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={mutation.isPending} onClick={handleSubmit((v) => mutation.mutate(v))}>
            {isEdit ? 'Save' : 'Add contact'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
        <TextField label="Name" required {...register('name')} error={!!errors.name} helperText={errors.name?.message} />
        <TextField
          label="E-mail"
          required
          {...register('email')}
          error={!!errors.email}
          helperText={errors.email?.message ?? 'Unique per partner.'}
        />
        <TextField label="Phone" {...register('phone')} error={!!errors.phone} helperText={errors.phone?.message} />
        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <TextField
              select
              label="Role"
              {...field}
              helperText="The claim link for the developer key goes to a technical contact."
            >
              <MenuItem value="technical">Technical</MenuItem>
              <MenuItem value="business">Business</MenuItem>
            </TextField>
          )}
        />
        <Controller
          name="is_active"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
              label="Active"
            />
          )}
        />
      </Box>
    </AppDialog>
  );
}

